import {
  formatHrPercentCell,
  hrValueStatusLabelTh,
  isHrImportBundleCapped,
  type HrImportDashboardBundle,
  type HrImportMeta,
} from '../services/hrImportData';
import { hrMetricDisplayLabel, hrUnitDisplayName } from '../lib/hrUnitDisplay';
import type { ExportCellValue, ExportWorkbookSheet } from '../services/exportService';

const YES_TH = 'ใช่';
const NO_TH = 'ไม่ใช่';
const EMPTY_FY_ROW = ['ไม่มีข้อมูลสำหรับปีงบประมาณนี้'] as const;
const HR_MISSING_VALUE_LEGEND = 'ไม่มีข้อมูล / ไม่เกี่ยวข้อง / สูตรไม่ถูกต้อง / เป็นศูนย์ (รอตรวจสอบ)';

const formatNumberCell = (value: number | null): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : 'ไม่มีข้อมูล';

const formatDateTimeTh = (value: HrImportMeta['lastSuccessfulSyncAt']): string => {
  const maybeTimestamp = value as { toDate?: () => Date } | null | undefined;
  const date = maybeTimestamp?.toDate?.();
  if (!date || Number.isNaN(date.getTime())) return 'ไม่มีข้อมูล';
  return date.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' });
};

const syncStatusLabelTh = (status: string | undefined): string => {
  switch (status) {
    case 'success':
      return 'สำเร็จ';
    case 'failed':
    case 'error':
      return 'ไม่สำเร็จ';
    case 'running':
    case 'processing':
      return 'กำลังประมวลผล';
    case 'partial':
      return 'สำเร็จบางส่วน';
    default:
      return status?.trim() || 'ไม่มีข้อมูล';
  }
};

const overviewSectionLabelTh = (sectionType: string): string => {
  switch (sectionType) {
    case 'monthlyStaffTypeCount':
      return 'จำนวนบุคลากรรายเดือนตามประเภท';
    case 'resignationReason':
      return 'เหตุผลการลาออก';
    case 'tenureBucket':
      return 'ช่วงอายุงาน';
    case 'unitTurnoverCount':
      return 'จำนวนการลาออกตามหน่วยงาน';
    case 'monthlyTurnoverRate':
      return 'อัตราการลาออกรายเดือน';
    default:
      return 'ข้อมูลภาพรวมรายเดือน';
  }
};

const metaRows = (
  bundle: HrImportDashboardBundle,
  fiscalYear: number,
): ExportCellValue[][] => [
  ['หมวดหมู่', 'ทรัพยากรบุคคล'],
  ['ปีงบประมาณ', String(fiscalYear)],
  ['ขอบเขตข้อมูล', 'ข้อมูลทรัพยากรบุคคลเป็นภาพรวมตามชุดนำเข้าที่ใช้งานอยู่'],
  ['สถานะซิงก์', syncStatusLabelTh(bundle.meta?.syncStatus)],
  ['ซิงก์สำเร็จล่าสุด', formatDateTimeTh(bundle.meta?.lastSuccessfulSyncAt)],
  ['จำนวนข้อมูลรายปีที่เขียนไว้', bundle.meta?.writtenRecordCount?.annualMetrics ?? 'ไม่มีข้อมูล'],
  ['จำนวนข้อมูลลาออกรายปีที่เขียนไว้', bundle.meta?.writtenRecordCount?.turnoverAnnual ?? 'ไม่มีข้อมูล'],
  ['จำนวนข้อมูลภาพรวมที่เขียนไว้', bundle.meta?.writtenRecordCount?.turnoverOverview ?? 'ไม่มีข้อมูล'],
  ['สถานะข้อมูลที่ขาดหาย', HR_MISSING_VALUE_LEGEND],
  ['หมายเหตุการจำกัดข้อมูล', isHrImportBundleCapped(bundle) ? 'ข้อมูลบางชุดอาจถูกจำกัดจำนวนแถวตามบริการอ่านข้อมูล' : 'ไม่พบสัญญาณว่าข้อมูลถูกจำกัดจำนวนแถว'],
];

const withEmptyPolicy = (
  rows: ExportCellValue[][],
): ExportCellValue[][] => rows.length > 0 ? rows : [[...EMPTY_FY_ROW]];

const filteredAnnualRows = (bundle: HrImportDashboardBundle, fiscalYear: number) =>
  bundle.annual
    .filter((row) => row.fiscalYear === fiscalYear)
    .sort((a, b) =>
      a.metricId.localeCompare(b.metricId, 'th') ||
      a.unitNameRaw.localeCompare(b.unitNameRaw, 'th') ||
      (a.departmentId ?? '').localeCompare(b.departmentId ?? '', 'th') ||
      a.id.localeCompare(b.id, 'th'),
    );

const filteredTurnoverRows = (bundle: HrImportDashboardBundle, fiscalYear: number) =>
  bundle.turnover
    .filter((row) => row.fiscalYear === fiscalYear)
    .sort((a, b) => a.fiscalYear - b.fiscalYear || a.id.localeCompare(b.id, 'th'));

const filteredOverviewRows = (bundle: HrImportDashboardBundle, fiscalYear: number) =>
  bundle.overview
    .filter((row) => row.fiscalYear === fiscalYear)
    .sort((a, b) =>
      a.sectionType.localeCompare(b.sectionType, 'th') ||
      (a.month ?? 0) - (b.month ?? 0) ||
      a.rowLabelRaw.localeCompare(b.rowLabelRaw, 'th') ||
      a.id.localeCompare(b.id, 'th'),
    );

export function buildHrExportSheets(
  bundle: HrImportDashboardBundle,
  fiscalYear: number,
): ExportWorkbookSheet[] {
  const sharedMetaRows = metaRows(bundle, fiscalYear);

  const annualRows: ExportCellValue[][] = filteredAnnualRows(bundle, fiscalYear).flatMap((row) => {
    const unitLabel = hrUnitDisplayName(row.unitNameRaw);
    if (!unitLabel) return [];
    return [[
    String(row.fiscalYear),
    hrMetricDisplayLabel(row.metricId, row.metricLabelTh),
    unitLabel,
    row.valueStatus === 'valid' && row.valuePercent != null ? row.valuePercent : null,
    hrValueStatusLabelTh(row.valueStatus),
    row.validForDashboard ? YES_TH : NO_TH,
  ]];
  });

  const turnoverRows: ExportCellValue[][] = filteredTurnoverRows(bundle, fiscalYear).map((row) => [
    String(row.fiscalYear),
    row.valueStatus === 'valid' && row.valuePercent != null ? row.valuePercent : null,
    hrValueStatusLabelTh(row.valueStatus),
  ]);

  const overviewRows: ExportCellValue[][] = filteredOverviewRows(bundle, fiscalYear).flatMap((row) => {
    const rowLabel = hrUnitDisplayName(row.rowLabelRaw);
    if (!rowLabel) return [];
    return [[
    String(row.fiscalYear),
    overviewSectionLabelTh(row.sectionType),
    rowLabel,
    row.month != null ? String(row.month) : '—',
    row.valueStatus === 'valid' && row.value != null ? row.value : null,
    row.valueStatus === 'valid' && row.valuePercent != null ? row.valuePercent : null,
    hrValueStatusLabelTh(row.valueStatus),
  ]];
  });

  return [
    {
      sheetName: 'คำอธิบาย',
      rows: [
        ...sharedMetaRows,
        [],
        ['คำอธิบายสถานะ', 'ความหมาย'],
        ['ถูกต้อง', 'ใช้คำนวณและแสดงในแดชบอร์ดได้'],
        ['ไม่มีข้อมูล', 'ไม่มีค่าจากแหล่งข้อมูล'],
        ['ไม่เกี่ยวข้อง', 'รายการนี้ไม่เกี่ยวข้องกับตัวชี้วัด'],
        ['สูตรไม่ถูกต้อง', 'สูตรจากแหล่งข้อมูลอ่านค่าไม่ได้'],
        ['เป็นศูนย์ (รอตรวจสอบ)', 'ค่าเป็นศูนย์และต้องตรวจสอบก่อนสรุปผล'],
      ],
      columnWidths: [28, 70],
    },
    {
      sheetName: 'ตัวชี้วัดรายปี',
      rows: [
        ...sharedMetaRows,
        [],
        ['ปีงบประมาณ', 'ตัวชี้วัด', 'หน่วยงาน', 'ค่า', 'สถานะ', 'ใช้ในแดชบอร์ด'],
        ...withEmptyPolicy(annualRows),
      ],
      autoFilter: annualRows.length > 0,
      freezeHeaderRows: sharedMetaRows.length + 2,
    },
    {
      sheetName: 'อัตราการลาออก',
      rows: [
        ...sharedMetaRows,
        [],
        ['ปีงบประมาณ', 'อัตราการลาออก', 'สถานะ'],
        ...withEmptyPolicy(turnoverRows),
      ],
      autoFilter: turnoverRows.length > 0,
      freezeHeaderRows: sharedMetaRows.length + 2,
    },
    {
      sheetName: 'ภาพรวมรายเดือน',
      rows: [
        ...sharedMetaRows,
        [],
        ['ปีงบประมาณ', 'ประเภทข้อมูล', 'รายการ', 'เดือน', 'ค่า', 'ร้อยละ', 'สถานะ'],
        ...withEmptyPolicy(overviewRows),
      ],
      autoFilter: overviewRows.length > 0,
      freezeHeaderRows: sharedMetaRows.length + 2,
    },
  ];
}

export function buildHrExportPdfData(
  bundle: HrImportDashboardBundle,
  fiscalYear: number,
): { headers: string[]; rows: string[][] } {
  const annualRows = filteredAnnualRows(bundle, fiscalYear).flatMap((row) => {
    const unitLabel = hrUnitDisplayName(row.unitNameRaw);
    if (!unitLabel) return [];
    const metricLabel = hrMetricDisplayLabel(row.metricId, row.metricLabelTh);
    return [[
    'ตัวชี้วัดรายปี',
    String(row.fiscalYear),
    `${metricLabel} - ${unitLabel}`,
    'ตัวชี้วัดรายปี',
    formatHrPercentCell({ valuePercent: row.valuePercent, valueStatus: row.valueStatus }),
    hrValueStatusLabelTh(row.valueStatus),
  ]];
  });

  const turnoverRows = filteredTurnoverRows(bundle, fiscalYear).map((row) => [
    'อัตราการลาออกรายปี',
    String(row.fiscalYear),
    'อัตราการลาออก',
    'อัตราการลาออกรายปี',
    formatHrPercentCell({ valuePercent: row.valuePercent, valueStatus: row.valueStatus }),
    hrValueStatusLabelTh(row.valueStatus),
  ]);

  const overviewRows = filteredOverviewRows(bundle, fiscalYear).flatMap((row) => {
    const rowLabel = hrUnitDisplayName(row.rowLabelRaw);
    if (!rowLabel) return [];
    return [[
    'ภาพรวมรายเดือน',
    String(row.fiscalYear),
    rowLabel,
    overviewSectionLabelTh(row.sectionType),
    row.valuePercent != null
      ? formatHrPercentCell({ valuePercent: row.valuePercent, valueStatus: row.valueStatus })
      : formatNumberCell(row.value),
    hrValueStatusLabelTh(row.valueStatus),
  ]];
  });

  const rows = [...annualRows, ...turnoverRows, ...overviewRows];
  return {
    headers: ['ชุดข้อมูล', 'ปีงบประมาณ', 'รายการ', 'ประเภท', 'ค่า', 'สถานะ'],
    rows: rows.length > 0 ? rows : [['ไม่มีข้อมูล', String(fiscalYear), 'ไม่มีข้อมูลสำหรับปีงบประมาณนี้', '-', '-', '-']],
  };
}
